/**
 * 智能状态计算引擎
 * 根据检查结果和参考范围智能计算状态，支持人均课时费和其他培训要素的差异化计算逻辑
 */

export interface StatusCalculationParams {
  actualValue: number;
  referenceMin?: number;
  referenceMax?: number;
  elementName: string;
  cityLevel: number;
}

export interface StatusResult {
  status: StatusEnum;
  color: string;
  icon: string;
  text: string;
  description: string;
  deviationPercentage?: number;
}

export enum StatusEnum {
  NORMAL = 'normal',
  SLIGHTLY_HIGH = 'slightly_high',
  DANGEROUS = 'dangerous',
  SLIGHTLY_LOW = 'slightly_low',
  SIGNIFICANTLY_LOW = 'significantly_low'
}

export interface StatusConfig {
  status: StatusEnum;
  color: string;
  icon: string;
  text: string;
  description: string;
}

export class StatusCalculationEngine {
  private static readonly STATUS_CONFIGS: Record<StatusEnum, StatusConfig> = {
    [StatusEnum.NORMAL]: {
      status: StatusEnum.NORMAL,
      color: '#52c41a',
      icon: '✓',
      text: '正常',
      description: '检查结果在参考范围内'
    },
    [StatusEnum.SLIGHTLY_HIGH]: {
      status: StatusEnum.SLIGHTLY_HIGH,
      color: '#fa8c16',
      icon: '↗',
      text: '略高',
      description: '检查结果高于参考范围10%以内'
    },
    [StatusEnum.DANGEROUS]: {
      status: StatusEnum.DANGEROUS,
      color: '#f5222d',
      icon: '↑',
      text: '危险',
      description: '检查结果高于参考范围10%以上'
    },
    [StatusEnum.SLIGHTLY_LOW]: {
      status: StatusEnum.SLIGHTLY_LOW,
      color: '#52c41a',
      icon: '↘',
      text: '略低',
      description: '检查结果低于参考范围10%以内'
    },
    [StatusEnum.SIGNIFICANTLY_LOW]: {
      status: StatusEnum.SIGNIFICANTLY_LOW,
      color: '#1890ff',
      icon: '↓',
      text: '偏低',
      description: '检查结果低于参考范围10%以上'
    }
  };

  /**
   * 计算检查结果状态
   */
  static calculate(params: StatusCalculationParams): StatusResult {
    const { actualValue, referenceMin, referenceMax, elementName, cityLevel } = params;

    // 验证输入参数
    if (actualValue == null || isNaN(actualValue)) {
      throw new Error('实际值无效');
    }

    // 如果没有参考范围，返回正常状态
    if (referenceMin == null && referenceMax == null) {
      return this.createStatusResult(StatusEnum.NORMAL);
    }

    // 人均课时费的特殊处理逻辑
    if (this.isPerCapitaCost(elementName)) {
      return this.calculatePerCapitaCostStatus(actualValue, referenceMin, referenceMax, cityLevel);
    }

    // 其他培训要素的通用处理逻辑
    return this.calculateGeneralStatus(actualValue, referenceMin, referenceMax);
  }

  /**
   * 判断是否为人均课时费
   */
  private static isPerCapitaCost(elementName: string): boolean {
    return Boolean(elementName && elementName.includes('人均课时费'));
  }

  /**
   * 判断是否为一二线城市
   */
  private static isFirstTierCity(cityLevel: number): boolean {
    return cityLevel <= 2;
  }

  /**
   * 人均课时费状态计算
   */
  private static calculatePerCapitaCostStatus(
    actualValue: number,
    referenceMin?: number,
    referenceMax?: number,
    cityLevel?: number
  ): StatusResult {
    // 一二线城市：仅与参考值最大值进行比较
    if (this.isFirstTierCity(cityLevel || 5)) {
      if (referenceMax == null) {
        return this.createStatusResult(StatusEnum.NORMAL);
      }

      const compareValue = referenceMax;
      const deviationPercentage = ((actualValue - compareValue) / compareValue) * 100;

      // 等于参考值时为正常
      if (actualValue === compareValue) {
        return this.createStatusResult(StatusEnum.NORMAL, 0);
      }

      // 低于参考值
      if (actualValue < compareValue) {
        const absDeviation = Math.abs(deviationPercentage);
        if (absDeviation <= 10) {
          return this.createStatusResult(StatusEnum.SLIGHTLY_LOW, deviationPercentage);
        } else {
          return this.createStatusResult(StatusEnum.SIGNIFICANTLY_LOW, deviationPercentage);
        }
      }

      // 高于参考值
      if (deviationPercentage <= 10) {
        return this.createStatusResult(StatusEnum.SLIGHTLY_HIGH, deviationPercentage);
      } else {
        return this.createStatusResult(StatusEnum.DANGEROUS, deviationPercentage);
      }
    }

    // 其他城市：与参考范围进行比较
    return this.calculateGeneralStatus(actualValue, referenceMin, referenceMax);
  }

  /**
   * 通用状态计算（其他培训要素和非一二线城市的人均课时费）
   */
  private static calculateGeneralStatus(
    actualValue: number,
    referenceMin?: number,
    referenceMax?: number
  ): StatusResult {
    // 如果只有最大值
    if (referenceMin == null && referenceMax != null) {
      return this.compareWithSingleValue(actualValue, referenceMax, 'max');
    }

    // 如果只有最小值
    if (referenceMin != null && referenceMax == null) {
      return this.compareWithSingleValue(actualValue, referenceMin, 'min');
    }

    // 如果有完整范围
    if (referenceMin != null && referenceMax != null) {
      return this.compareWithRange(actualValue, referenceMin, referenceMax);
    }

    // 没有有效的参考值
    return this.createStatusResult(StatusEnum.NORMAL);
  }

  /**
   * 与单个参考值进行比较
   */
  private static compareWithSingleValue(
    actualValue: number,
    referenceValue: number,
    type: 'min' | 'max'
  ): StatusResult {
    if (actualValue === referenceValue) {
      return this.createStatusResult(StatusEnum.NORMAL, 0);
    }

    const deviationPercentage = ((actualValue - referenceValue) / referenceValue) * 100;

    if (type === 'max') {
      // 与最大值比较
      if (actualValue <= referenceValue) {
        return this.createStatusResult(StatusEnum.NORMAL, 0);
      } else {
        // 高于最大值
        if (deviationPercentage <= 10) {
          return this.createStatusResult(StatusEnum.SLIGHTLY_HIGH, deviationPercentage);
        } else {
          return this.createStatusResult(StatusEnum.DANGEROUS, deviationPercentage);
        }
      }
    } else {
      // 与最小值比较
      if (actualValue >= referenceValue) {
        return this.createStatusResult(StatusEnum.NORMAL, 0);
      } else {
        // 低于最小值
        const absDeviation = Math.abs(deviationPercentage);
        if (absDeviation <= 10) {
          return this.createStatusResult(StatusEnum.SLIGHTLY_LOW, deviationPercentage);
        } else {
          return this.createStatusResult(StatusEnum.SIGNIFICANTLY_LOW, deviationPercentage);
        }
      }
    }
  }

  /**
   * 与参考范围进行比较
   */
  private static compareWithRange(
    actualValue: number,
    referenceMin: number,
    referenceMax: number
  ): StatusResult {
    // 在正常范围内（包括边界值）
    if (actualValue >= referenceMin && actualValue <= referenceMax) {
      return this.createStatusResult(StatusEnum.NORMAL, 0);
    }

    // 低于最小值
    if (actualValue < referenceMin) {
      const deviationPercentage = ((actualValue - referenceMin) / referenceMin) * 100;
      const absDeviation = Math.abs(deviationPercentage);

      if (absDeviation <= 10) {
        return this.createStatusResult(StatusEnum.SLIGHTLY_LOW, deviationPercentage);
      } else {
        return this.createStatusResult(StatusEnum.SIGNIFICANTLY_LOW, deviationPercentage);
      }
    }

    // 高于最大值
    if (actualValue > referenceMax) {
      const deviationPercentage = ((actualValue - referenceMax) / referenceMax) * 100;

      if (deviationPercentage <= 10) {
        return this.createStatusResult(StatusEnum.SLIGHTLY_HIGH, deviationPercentage);
      } else {
        return this.createStatusResult(StatusEnum.DANGEROUS, deviationPercentage);
      }
    }

    // 理论上不会到达这里
    return this.createStatusResult(StatusEnum.NORMAL, 0);
  }

  /**
   * 创建状态结果
   */
  private static createStatusResult(status: StatusEnum, deviationPercentage?: number): StatusResult {
    const config = this.STATUS_CONFIGS[status];

    return {
      status: config.status,
      color: config.color,
      icon: config.icon,
      text: config.text,
      description: config.description,
      deviationPercentage: deviationPercentage !== undefined ? Math.round(deviationPercentage * 100) / 100 : undefined
    };
  }

  /**
   * 获取状态配置
   */
  static getStatusConfig(status: StatusEnum): StatusConfig {
    return this.STATUS_CONFIGS[status];
  }

  /**
   * 获取所有状态配置
   */
  static getAllStatusConfigs(): Record<StatusEnum, StatusConfig> {
    return this.STATUS_CONFIGS;
  }
}